import { useContext } from 'react';
import { TextSelection } from '@tiptap/pm/state';
import { calculateScrollOffset, findElementByAttribute } from '@shared/helpers';
import DocumentContext from '../contexts/DocumentContext.context';
import { useItemScreenStore } from '@modules/items';
import { TScrollArgs, useScrollContainer } from '@shared/hooks';
import { DOCUMENT_HTML_ATTRIBUTES } from '../constants';
import { ToastContext } from '@shared/contexts';
import { findQuoteMatch } from '../helpers/scrollContent/domQuoteSearch';
import {
  buildCandidateProseMirrorRanges,
  findProseMirrorQuoteMatch,
  scrollRectForProseMirrorRange,
} from '../helpers/scrollContent/proseMirrorQuoteSearch';
import { clearPulseHighlights, PULSE_HIGHLIGHT_CLASS } from '../helpers/scrollContent/pulseHighlight';
import { activateElementId, holdScrollTarget, isLargeDocument, whenSwapDone } from '../largeDocument';
import { findByIdAttribute, resolveElementRangeById, uniqueIds } from '../helpers/scrollContent/scrollTargets';
import { trace } from '../largeDocument/trace';
import type { TResolvedElementRange, TTextMatch } from '../helpers/scrollContent/types';

type TScrollContentArgs = Omit<TScrollArgs, 'value' | 'highlightText'> & {
  clearHighlight?: boolean;
  highlightText?: string | string[];
  /** Find and scroll to the targets, but mark nothing. */
  noHighlight?: boolean;
  value?: string;
  values?: string[];
};

/**
 * useScrollContent
 * ----------------
 * Hook for scrolling *within* the Tiptap editor. This also sets the Tiptap
 * text selection to the found element.
 *
 * It's an alternative to a more general `useScrollContainer` hook.
 */
const useScrollContent = () => {
  const scrollContainer = useScrollContainer();
  const documentContext = useContext(DocumentContext);
  const { show } = useContext(ToastContext);
  const { viewChanges } = useItemScreenStore();

  // If the document context is not available, return an empty function
  if (!documentContext) return () => {};

  const { editor, refContentNode } = documentContext;

  const scrollContent = (args: TScrollContentArgs) => {
    if (!editor || !refContentNode.current) return;

    const {
      attribute = viewChanges ? DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID : DOCUMENT_HTML_ATTRIBUTES.DEFAULT_ID,
      clearHighlight = false,
      offset = 100,
      value,
      values = [],
      noFocus = false,
      highlightText,
      noHighlight = false,
      shouldHighlight = false,
    } = args;

    // When in post-change impact analysis view, use scrollContainer with the same ref
    if (viewChanges) {
      if (!value) return;
      scrollContainer({ containerRef: refContentNode, attribute, value, offset });
      return;
    }

    // Otherwise, handle the TipTap-specific scrolling and text selection.
    const editorContainer = refContentNode.current.view;
    const { highlight: hlMark } = editor.schema.marks;
    const highlightTexts = Array.isArray(highlightText)
      ? highlightText.filter(Boolean)
      : highlightText
        ? [highlightText]
        : [];
    const shouldClearPulseHighlights = clearHighlight || shouldHighlight || highlightTexts.length > 0;
    const tr = shouldClearPulseHighlights ? clearPulseHighlights(editor, PULSE_HIGHLIGHT_CLASS) : editor.view.state.tr;
    let element: HTMLElement | null = null;
    let selectionPos: number | undefined;
    let scrollRect: DOMRect | undefined;
    const orderedElementIds = uniqueIds([value, ...values]);
    const resolvedElementRanges = orderedElementIds
      .map((id) => resolveElementRangeById(editor, id))
      .filter((range): range is TResolvedElementRange => Boolean(range));
    const candidateProseMirrorRanges = buildCandidateProseMirrorRanges(editor, resolvedElementRanges);

    // The caller's attribute first: a comment id is not an element id.
    const findInEditor = (id: string) => findByIdAttribute(editor.view.dom, id, attribute);

    // Every id, not only `value`: a citation names its targets in `values` and carries no `elem`.
    const anyHere = resolvedElementRanges.length > 0 || orderedElementIds.some((id) => findInEditor(id) !== null);
    trace('scroll:asked', { attribute, targets: orderedElementIds, alreadyOnScreen: anyHere });
    // Only when none is on screen: a citation can name ids in two parts, which would swap for ever.
    const loadedFor = anyHere ? undefined : orderedElementIds.find((id) => activateElementId(id));
    if (loadedFor) {
      trace('scroll:loading-part', { forId: loadedFor });
      whenSwapDone(() => scrollContent({ ...args, clearHighlight: false }));
      return;
    }

    if (value) {
      element = findInEditor(value);

      if (element) {
        // -1 means ProseMirror does not map this node - never hand that to `TextSelection.create`.
        const pos = editor.view.posAtDOM(element, 0);
        if (pos >= 0) selectionPos = pos;
        scrollRect = element.getBoundingClientRect();
      }
    }

    const quoteMatch =
      findProseMirrorQuoteMatch(editor, candidateProseMirrorRanges, highlightTexts) ??
      (orderedElementIds.length > 1
        ? null
        : highlightTexts.reduce<TTextMatch | null>(
            (currentMatch, needle) => currentMatch ?? findQuoteMatch(editor, element, needle),
            null
          ));

    if (!scrollRect && candidateProseMirrorRanges.length > 0) {
      const fallbackRange = candidateProseMirrorRanges[candidateProseMirrorRanges.length - 1];
      const fallbackRect = scrollRectForProseMirrorRange(editor, fallbackRange.from, fallbackRange.to);
      if (fallbackRect) {
        selectionPos = fallbackRange.from;
        scrollRect = fallbackRect;
      }
    }

    // An explicit target that resolved to nothing (stale/deleted) tells the user instead of a dead click.
    if (
      quoteMatch === null &&
      scrollRect === undefined &&
      (Boolean(value) || values.length > 0 || highlightTexts.length > 0)
    ) {
      if (tr.steps.length) editor.view.dispatch(tr); // still clear the previous highlight before bailing
      trace('scroll:not-found', { value, values, highlightTexts: highlightTexts.length });
      console.error(
        `Unable to locate scroll target. value="${value ?? ''}" values=${values.length} highlightTexts=${highlightTexts.length}.`
      );
      show('error', 'Unable to locate the section. It might no longer be available.');
      return;
    }

    /**
     * Full-element highlight (optional).
     * Notice: marks can only live on the inline content. So it's not possible to highlight e.g. a heading
     */
    if (shouldHighlight && !noHighlight && selectionPos !== undefined) {
      tr.addMark(selectionPos, selectionPos + 1, hlMark.create({ class: PULSE_HIGHLIGHT_CLASS }));
    }

    // text-range highlight, if requested
    if (quoteMatch !== null) {
      if (!noHighlight) tr.addMark(quoteMatch.from, quoteMatch.to, hlMark.create({ class: PULSE_HIGHLIGHT_CLASS }));
      selectionPos = quoteMatch.from;
      scrollRect = quoteMatch.scrollRect;
    }

    // apply selection *before* scroll so browser paints once
    if (!noFocus && selectionPos !== undefined) {
      // `near`, not `create`: a comment can be anchored to a whole node rather than to text, and
      // `TextSelection.create` THROWS on a position that is not in inline content - "TextSelection
      // endpoint not pointing into a node with inline content (selection-node)", which aborted the
      // scroll entirely, so clicking such a comment did nothing. `near` finds the closest place a
      // selection can legally sit instead.
      tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(selectionPos, tr.doc.content.size))));
    }

    // A part-loaded document is still settling after a swap, and both steps below move the view: the
    // dispatch re-renders, and focusing scrolls the selection into sight. Done in this order they
    // undo the scroll just made, which is seen as scrolling twice.
    const parted = isLargeDocument();
    let intendedScrollTop: number | null = null;

    if (scrollRect) {
      const containerRect = editorContainer.getBoundingClientRect();
      const currentOffset = calculateScrollOffset(scrollRect, containerRect, editorContainer.scrollTop, offset);
      // Instant for the whole document, not only across a swap: a smooth scroll to a target in
      // ANOTHER part cannot start until that part loads, so mixing the two is worse than neither.
      editorContainer.scrollTo({ top: currentOffset, behavior: parted ? 'auto' : 'smooth' });
      if (parted) intendedScrollTop = editorContainer.scrollTop;
    }

    // Must come after the scroll, otherwise not scrolling.
    // `selectionSet` as well as the steps: moving the selection adds no step, so a click with no
    // highlight to draw would never apply it, and focusing then scrolls the OLD selection back.
    if (tr.steps.length || tr.selectionSet) editor.view.dispatch(tr);
    if (!noFocus && selectionPos !== undefined) editor.view.focus();

    if (intendedScrollTop !== null) {
      // Same task as the moves above, so nothing paints in between.
      if (Math.abs(editorContainer.scrollTop - intendedScrollTop) > 1) editorContainer.scrollTop = intendedScrollTop;
      // Then hold it there while node views mount, static halves re-place and images resolve. Keyed
      // on whatever resolved, not on `value`: a citation arrives with ids only and got no hold.
      const heldId = value ?? resolvedElementRanges[0]?.id;
      if (heldId) holdScrollTarget(editorContainer, heldId);
    }
  };

  return scrollContent;
};

export default useScrollContent;
